import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  LayoutDashboard, CalendarDays, DollarSign, LogOut, Users,
  CheckCircle2, Clock, Loader2, RefreshCw,
  Phone, Mail, MapPin, Trash2, Eye, CalendarRange, CalendarClock, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  '06:00 AM', '06:30 AM', '07:00 AM', '07:30 AM', '08:00 AM', '08:30 AM',
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
  '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
  '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM', '08:30 PM',
  '09:00 PM', '09:30 PM', '10:00 PM'
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleDateOpen, setRescheduleDateOpen] = useState(false);

  const token = localStorage.getItem('adminToken');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyToken();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify`, authHeaders);
      loadData();
    } catch (error) {
      localStorage.removeItem('adminToken');
      navigate('/admin');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`, authHeaders),
        axios.get(`${API}/stats`, authHeaders)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { booking_status: status }, authHeaders);
      toast.success('Booking status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, authHeaders);
      toast.success('Booking deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const openReschedule = (booking) => {
    setRescheduleBooking(booking);
    setRescheduleDate(booking.pickup_date ? new Date(booking.pickup_date + 'T00:00:00') : new Date());
    setRescheduleTime(booking.pickup_time || '');
    setShowReschedule(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      await axios.put(`${API}/bookings/${rescheduleBooking.id}`, {
        pickup_date: format(rescheduleDate, 'yyyy-MM-dd'),
        pickup_time: rescheduleTime
      }, authHeaders);
      toast.success('Booking rescheduled');
      setShowReschedule(false);
      setRescheduleBooking(null);
      if (rescheduleDate) setCalendarDate(rescheduleDate);
      loadData();
    } catch (error) {
      toast.error('Failed to reschedule booking');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-600/20 text-green-400 border-green-500/30';
      case 'completed': return 'bg-blue-600/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-600/20 text-red-400 border-red-500/30';
      default: return 'bg-amber-600/20 text-amber-400 border-amber-500/30';
    }
  };

  const getPaymentColor = (status) => {
    return status === 'paid' ? 'bg-green-600/20 text-green-400 border-green-500/30' : 'bg-amber-600/20 text-amber-400 border-amber-500/30';
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-950 text-white p-6 hidden lg:block border-r border-slate-800">
        <div className="flex items-center gap-3 mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1iyuca53_IMG_9172.png" 
            alt="MarcoPolo Logo" 
            className="w-10 h-10 object-contain rounded-full"
          />
          <div>
            <h1 className="font-bold">MarcoPolo</h1>
            <p className="text-xs text-slate-400">Admin Panel</p>
          </div>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            data-testid="tab-dashboard"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === 'dashboard' ? 'bg-red-600' : 'hover:bg-slate-800'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            data-testid="tab-bookings"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === 'bookings' ? 'bg-red-600' : 'hover:bg-slate-800'
            }`}
          >
            <CalendarDays className="w-5 h-5" />
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            data-testid="tab-calendar"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === 'calendar' ? 'bg-red-600' : 'hover:bg-slate-800'
            }`}
          >
            <CalendarRange className="w-5 h-5" />
            Calendar
          </button>
        </nav>

        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="absolute bottom-6 left-6 right-6 flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-800 transition-all text-slate-400 hover:text-white"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {activeTab === 'dashboard' ? 'Dashboard' : activeTab === 'calendar' ? 'Calendar' : 'Bookings'}
            </h1>
            <p className="text-slate-400">Manage your transportation bookings</p>
          </div>
          <Button onClick={loadData} variant="outline" className="gap-2 bg-transparent border-slate-700 text-white hover:bg-slate-800" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-red-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{stats.total_bookings}</p>
                <p className="text-slate-400">Total Bookings</p>
              </div>

              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-600/20 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{stats.pending_bookings}</p>
                <p className="text-slate-400">Pending</p>
              </div>

              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{stats.confirmed_bookings}</p>
                <p className="text-slate-400">Confirmed</p>
              </div>

              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">${stats.total_revenue.toFixed(2)}</p>
                <p className="text-slate-400">Revenue</p>
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-lg font-bold text-white">Recent Bookings</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Service</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Price</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {bookings.slice(0, 5).map((booking) => (
                      <tr key={booking.id} className="hover:bg-slate-700/50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-white">{booking.customer_name}</p>
                          <p className="text-sm text-slate-400">{booking.customer_email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="capitalize text-slate-300">{booking.service_type?.replace('_', ' ')}</span>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{booking.pickup_date}</td>
                        <td className="px-6 py-4 font-semibold text-white">${booking.estimated_price?.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${getStatusColor(booking.booking_status)}`}>
                            {booking.booking_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Route</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Service</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Schedule</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Payment</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-slate-700/50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-white">{booking.customer_name}</p>
                        <p className="text-sm text-slate-400">{booking.customer_phone}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-300 truncate max-w-[200px]">{booking.pickup_address}</p>
                        <p className="text-xs text-slate-500">{booking.distance_miles} miles</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="capitalize text-slate-300 text-sm">{booking.service_type?.replace('_', ' ')}</span>
                        <p className="text-xs text-slate-500 capitalize">{booking.trip_type?.replace('_', ' ')}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-300">{booking.pickup_date}</p>
                        <p className="text-xs text-red-400">{booking.pickup_time}</p>
                      </td>
                      <td className="px-6 py-4 font-semibold text-white">${booking.estimated_price?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${getPaymentColor(booking.payment_status)}`}>
                          {booking.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          value={booking.booking_status}
                          onValueChange={(value) => updateBookingStatus(booking.id, value)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs bg-slate-700 border-slate-600 text-white" data-testid={`status-select-${booking.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-700 border-slate-600">
                            <SelectItem value="pending" className="text-white hover:bg-slate-600">Pending</SelectItem>
                            <SelectItem value="confirmed" className="text-white hover:bg-slate-600">Confirmed</SelectItem>
                            <SelectItem value="completed" className="text-white hover:bg-slate-600">Completed</SelectItem>
                            <SelectItem value="cancelled" className="text-white hover:bg-slate-600">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setSelectedBooking(booking); setShowDetails(true); }}
                            data-testid={`view-booking-${booking.id}`}
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                            onClick={() => deleteBooking(booking.id)}
                            data-testid={`delete-booking-${booking.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <div className="text-center py-12">
                  <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No bookings yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (() => {
          const dayStr = format(calendarDate, 'yyyy-MM-dd');
          const dayBookings = bookings
            .filter((b) => b.pickup_date === dayStr)
            .slice()
            .sort((a, b) => (a.pickup_time || '').localeCompare(b.pickup_time || ''));
          const bookedDays = [...new Set(bookings.map((b) => b.pickup_date).filter(Boolean))]
            .map((s) => new Date(s + 'T00:00:00'));
          const dayRevenue = dayBookings.reduce((sum, b) => sum + (b.estimated_price || 0), 0);
          return (
            <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start" data-testid="calendar-view">
              {/* Calendar */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 p-4">
                <CalendarComponent
                  mode="single"
                  selected={calendarDate}
                  onSelect={(d) => d && setCalendarDate(d)}
                  modifiers={{ booked: bookedDays }}
                  modifiersClassNames={{ booked: 'bg-red-600/30 text-red-300 font-bold rounded-md' }}
                  className="bg-slate-800 text-white"
                  data-testid="admin-calendar"
                />
                <div className="flex items-center gap-2 mt-4 px-2 text-xs text-slate-400">
                  <span className="w-3 h-3 rounded bg-red-600/30 border border-red-500/40 inline-block" />
                  Days with bookings
                </div>
              </div>

              {/* Day bookings panel */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="p-6 border-b border-slate-700 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-white" data-testid="calendar-day-title">
                      {format(calendarDate, 'EEEE, MMMM d, yyyy')}
                    </h2>
                    <p className="text-sm text-slate-400" data-testid="calendar-day-count">
                      {dayBookings.length} ride{dayBookings.length !== 1 ? 's' : ''} booked
                      {dayBookings.length > 0 && ` · $${dayRevenue.toFixed(2)} total`}
                    </p>
                  </div>
                </div>

                {dayBookings.length === 0 ? (
                  <div className="text-center py-16">
                    <CalendarRange className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No rides booked on this day</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className="p-5" data-testid={`calendar-booking-${booking.id}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex items-start gap-4">
                            <div className="flex flex-col items-center justify-center bg-red-600/20 border border-red-500/30 rounded-xl px-3 py-2 min-w-[90px]">
                              <Clock className="w-4 h-4 text-red-400 mb-1" />
                              <span className="text-sm font-bold text-white">{booking.pickup_time}</span>
                            </div>
                            <div className="space-y-1">
                              <p className="font-semibold text-white">{booking.customer_name}</p>
                              <p className="flex items-center gap-2 text-sm text-slate-300">
                                <Phone className="w-3.5 h-3.5 text-slate-400" /> {booking.customer_phone}
                              </p>
                              <p className="flex items-start gap-2 text-sm text-slate-400">
                                <MapPin className="w-3.5 h-3.5 text-green-500 mt-0.5" /> {booking.pickup_address}
                              </p>
                              <p className="flex items-start gap-2 text-sm text-slate-400">
                                <MapPin className="w-3.5 h-3.5 text-red-500 mt-0.5" /> {booking.dropoff_address}
                              </p>
                              <div className="flex items-center gap-3 pt-1">
                                <span className="text-sm font-bold text-emerald-400">${booking.estimated_price?.toFixed(2)}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize border ${getStatusColor(booking.booking_status)}`}>
                                  {booking.booking_status}
                                </span>
                                <span className="capitalize text-xs text-slate-500">{booking.service_type?.replace('_', ' ')}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReschedule(booking)}
                              data-testid={`reschedule-btn-${booking.id}`}
                              className="gap-2 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                            >
                              <CalendarClock className="w-4 h-4" /> Reschedule
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={booking.booking_status === 'cancelled'}
                              onClick={() => {
                                if (window.confirm('Cancel this booking?')) updateBookingStatus(booking.id, 'cancelled');
                              }}
                              data-testid={`cancel-btn-${booking.id}`}
                              className="gap-2 bg-transparent border-red-500/40 text-red-400 hover:bg-red-600/20 disabled:opacity-40"
                            >
                              <Ban className="w-4 h-4" /> Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </main>

      {/* Booking Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Customer</p>
                  <p className="font-semibold text-white">{selectedBooking.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Contact</p>
                  <p className="font-semibold text-white">{selectedBooking.customer_phone}</p>
                  <p className="text-sm text-slate-300">{selectedBooking.customer_email}</p>
                </div>
              </div>

              <div className="p-4 bg-slate-700/50 rounded-xl space-y-3 border border-slate-600">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-400">Pickup</p>
                    <p className="text-sm text-white">{selectedBooking.pickup_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-400">Drop-off</p>
                    <p className="text-sm text-white">{selectedBooking.dropoff_address}</p>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Service</p>
                  <p className="font-semibold capitalize text-white">{selectedBooking.service_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Trip Type</p>
                  <p className="font-semibold capitalize text-white">{selectedBooking.trip_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Distance</p>
                  <p className="font-semibold text-white">{selectedBooking.distance_miles} miles</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Date & Time</p>
                  <p className="font-semibold text-white">{selectedBooking.pickup_date} at {selectedBooking.pickup_time}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Price</p>
                  <p className="font-semibold text-xl text-red-500">${selectedBooking.estimated_price?.toFixed(2)}</p>
                </div>
              </div>

              {selectedBooking.special_instructions && (
                <div>
                  <p className="text-sm text-slate-400">Special Instructions</p>
                  <p className="p-3 bg-amber-600/20 rounded-lg text-sm text-amber-300 border border-amber-500/30">{selectedBooking.special_instructions}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reschedule Booking</DialogTitle>
          </DialogHeader>
          {rescheduleBooking && (
            <div className="space-y-5">
              <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                <p className="font-semibold text-white">{rescheduleBooking.customer_name}</p>
                <p className="text-sm text-slate-400">
                  Currently: {rescheduleBooking.pickup_date} at {rescheduleBooking.pickup_time}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">New Date</Label>
                <Popover open={rescheduleDateOpen} onOpenChange={setRescheduleDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="reschedule-date-btn"
                      className={`w-full h-11 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600 ${!rescheduleDate && 'text-slate-400'}`}
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-600" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={rescheduleDate}
                      onSelect={(d) => { setRescheduleDate(d); setRescheduleTime(''); setRescheduleDateOpen(false); }}
                      disabled={(date) => {
                        const startToday = new Date();
                        startToday.setHours(0, 0, 0, 0);
                        return date < startToday;
                      }}
                      initialFocus
                      className="bg-slate-800 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">New Time</Label>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="h-11 bg-slate-700 border-slate-600 text-white" data-testid="reschedule-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-64">
                    {(() => {
                      const slots = TIME_SLOTS.filter((time) => {
                        if (!rescheduleDate) return true;
                        const now = new Date();
                        if (rescheduleDate.toDateString() !== now.toDateString()) return true;
                        const [t, meridiem] = time.split(' ');
                        let [h, m] = t.split(':').map(Number);
                        if (meridiem === 'PM' && h !== 12) h += 12;
                        if (meridiem === 'AM' && h === 12) h = 0;
                        const slot = new Date(rescheduleDate);
                        slot.setHours(h, m, 0, 0);
                        return slot > now;
                      });
                      if (slots.length === 0) {
                        return <div className="px-3 py-2 text-sm text-slate-400">No more slots today — pick a future date</div>;
                      }
                      return slots.map((time) => (
                        <SelectItem key={time} value={time} className="text-white hover:bg-slate-600">{time}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReschedule(false)}
                  className="flex-1 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                  data-testid="reschedule-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReschedule}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="reschedule-save-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
